import React, { FC, Fragment, MouseEvent, useState } from 'react';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  CreateGroupModeratorMutation,
  DeleteGroupModeratorMutation,
  FeedKey,
  GroupContentApprovalType,
  GroupFragment,
  GroupRole,
  PublicUserProfileFragment,
  useCreateGroupModeratorMutation,
  useDeleteGroupModeratorMutation,
  UserType,
  useUnfollowMutation,
} from 'api';
import { useCapabilities, useCurrentUser, useNotify, useReleases } from 'hooks';
import { useUnfollow, useUpdateGroupRole } from 'features/groups/updaters';

interface IProps {
  group: GroupFragment;
  targetUser: PublicUserProfileFragment;
  role: GroupRole;
  moderatorCount: number;
}

const MemberActions: FC<IProps> = ({ group, targetUser, role, moderatorCount }) => {
  const { t } = useTranslation();
  const { isAdmin, needs } = useCapabilities();
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const releases = useReleases();

  const [anchorEl, setAnchorEl] = useState(null);

  const has4EyeModeration = releases.includes('4EyeModeration');
  const isTargetUserCurrentOrAdmin =
    targetUser.id === currentUser.id || targetUser.user_type === UserType.Admin;
  const isTargetUserModerator = role === GroupRole.Moderator;
  const isModerationTypeLocked = group.content_approval_type === GroupContentApprovalType.FourEyes;

  // TODO: This is quite the mess of logic
  const isCurrentUserModerator = needs('Access.Moderator', group.id);
  const canRemoveUser = isAdmin || (isCurrentUserModerator && !isTargetUserModerator);
  const i18nValues = { name: targetUser.full_name, title: group.title };

  const createModeratorMutationOptions = useUpdateGroupRole<CreateGroupModeratorMutation>(
    GroupRole.Moderator
  );
  const { mutate: createGroupModeratorMutation } = useCreateGroupModeratorMutation({
    onSuccess: () => {
      const message = t('groups:setModeratorNotify', i18nValues);
      notify.info({ message });
    },
    ...createModeratorMutationOptions,
  });

  const deleteModeratorMutationOptions = useUpdateGroupRole<DeleteGroupModeratorMutation>(
    GroupRole.Member
  );
  const { mutate: deleteGroupModeratorMutation } = useDeleteGroupModeratorMutation({
    onSuccess: () => {
      const message = t('groups:setMemberNotify', i18nValues);
      notify.info({ message });
    },
    ...deleteModeratorMutationOptions,
  });

  const unfollowMutationOptions = useUnfollow();
  const { mutate: unfollowMutation } = useUnfollowMutation({
    onSuccess: () => {
      const message = t('groups:removeUserNotify', i18nValues);
      notify.info({ message });
    },
    ...unfollowMutationOptions,
  });

  function handleOpen(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleMemberToModerator() {
    handleClose();

    createGroupModeratorMutation({
      user_id: targetUser.id,
      group_id: group.id,
    });
  }

  function handleModeratorToMember() {
    handleClose();

    deleteGroupModeratorMutation({
      user_id: targetUser.id,
      group_id: group.id,
    });
  }

  function handleRemoveMember() {
    handleClose();

    unfollowMutation({
      id: group.id,
      key: FeedKey.Group,
      target_user_id: targetUser.id,
    });
  }

  function renderAdminOptions() {
    return (
      <Fragment>
        {isTargetUserModerator && (
          <MenuItem onClick={handleModeratorToMember}>{t('groups:setMember')}</MenuItem>
        )}
        {!isTargetUserModerator && (
          <MenuItem onClick={handleMemberToModerator}>{t('groups:setModerator')}</MenuItem>
        )}
        <MenuItem onClick={handleRemoveMember}>{t('groups:removeUser')}</MenuItem>
      </Fragment>
    );
  }

  function renderModeratorOptions() {
    return <MenuItem onClick={handleRemoveMember}>{t('groups:removeUser')}</MenuItem>;
  }

  if (isTargetUserCurrentOrAdmin || !canRemoveUser) {
    return null;
  }

  if (isTargetUserModerator && has4EyeModeration && isModerationTypeLocked && moderatorCount <= 2) {
    return null;
  }

  return (
    <Fragment>
      <IconButton onClick={handleOpen} size="large">
        <ArrowDropDownRoundedIcon />
      </IconButton>
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
        {isAdmin ? renderAdminOptions() : renderModeratorOptions()}
      </Menu>
    </Fragment>
  );
};

export default MemberActions;
